import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleNotch } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import AuthShell from '../components/layout/AuthShell';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import { formatApiErrorDetail } from '../lib/utils';

function verifyStorageKey(token) {
  return `omnichek-email-verify:${token}`;
}

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const { clearSessionForVerify } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = useMemo(() => params.get('token') || '', [params]);
  const startedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    const tok = tokenFromUrl.trim();
    if (!tok) return undefined;

    const storageKey = verifyStorageKey(tok);
    const verifyState = sessionStorage.getItem(storageKey);
    if (verifyState === 'ok') {
      navigate('/login', {
        replace: true,
        state: {
          emailVerifiedOk: true,
          accountCreatedOk: true,
          verifiedEmail: sessionStorage.getItem(`${storageKey}:email`) || '',
        },
      });
      return undefined;
    }

    // Prevent StrictMode / auth re-render from restarting or stalling mid-flight
    if (startedRef.current) return undefined;
    startedRef.current = true;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        clearSessionForVerify();
        const res = await authApi.verifyEmail(tok);
        const verifiedEmail = res.data?.email || '';
        sessionStorage.setItem(storageKey, 'ok');
        if (verifiedEmail) {
          sessionStorage.setItem(`${storageKey}:email`, verifiedEmail);
        }
        if (cancelled) {
          // Still mark success so a remount can redirect
          return;
        }
        setDone(true);
        navigate('/login', {
          replace: true,
          state: {
            emailVerifiedOk: true,
            accountCreatedOk: !!res.data?.account_created,
            verifiedEmail,
          },
        });
      } catch (err) {
        startedRef.current = false;
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(`${storageKey}:email`);
        if (!cancelled) {
          setError(formatApiErrorDetail(err.response?.data?.detail) || t('auth.verifyEmailFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally omit clearSessionForVerify — it may change identity and re-trigger the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl, navigate, t]);

  return (
    <AuthShell
      title={t('auth.verifyEmailTitle')}
      description={t('auth.verifyEmailDesc')}
      footer={(
        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
            {t('auth.backToSignIn')}
          </Link>
        </p>
      )}
    >
      {!tokenFromUrl.trim() ? (
        <div className="space-y-4 text-center text-sm text-muted-foreground">
          <p>{t('auth.verifyEmailMissingToken')}</p>
        </div>
      ) : (
        <div className="space-y-4 text-center">
          {loading && !done && !error && (
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <CircleNotch className="h-8 w-8 animate-spin text-brand-600" />
              <p className="text-sm font-medium text-foreground">{t('auth.verifyEmailWorking')}</p>
              <p className="max-w-sm text-xs text-muted-foreground">{t('auth.verifyEmailWorkingHint')}</p>
            </div>
          )}
          {done && !error && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {t('auth.verifyEmailSuccess')}
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </div>
          )}
          {error && (
            <Button type="button" variant="outline" className="w-full" onClick={() => navigate('/login')}>
              {t('auth.backToSignIn')}
            </Button>
          )}
        </div>
      )}
    </AuthShell>
  );
}
